import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  Injector,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { IonApp, IonLoading, IonRouterOutlet, Platform } from '@ionic/angular';
import { SupabaseClient } from '@supabase/supabase-js';
import config from 'capacitor.config';
import {
  forgotPasswordRoutePath,
  getFavoritesRoutePath,
  getLoginRoutePath,
  getRoutePath,
  loginRoutePath,
  resetPasswordRoutePath,
  searchRoutePath,
  verseRoutePath,
  feedRoutePath,
  postRoutePath,
} from './app.routes';
import { AlertService } from './common/alert/alert.service';
import { UserService } from './common/user/user.service';
import { AppService } from './common/capacitor/app.service';
import { CreditsService } from './common/credits/credits.service';
import { AppleAuthService } from './common/capacitor/apple-auth.service';
import { CapacitorService } from './common/capacitor/capacitor.service';
import { GoogleAuthService } from './common/capacitor/google-auth.service';
import { ScreenOrientationService } from './common/capacitor/screen-orientation.service';
import { LoadingService } from './common/loading/loading.service';
import { LoggingService } from './common/logging/logging.service';
import { StatusBarService } from './common/theme/status-bar.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonLoading, IonApp, IonRouterOutlet, AsyncPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  loading$ = inject(LoadingService).loading$;
  private alertService = inject(AlertService);
  private userService = inject(UserService);
  private appService = inject(AppService);
  private creditsService = inject(CreditsService);
  private cdr = inject(ChangeDetectorRef);
  private appleAuthService = inject(AppleAuthService);
  private capacitorService = inject(CapacitorService);
  private googleAuthService = inject(GoogleAuthService);
  private injector = inject(Injector);
  private platform = inject(Platform);
  private screenOrientation = inject(ScreenOrientationService);
  private statusBarService = inject(StatusBarService);
  private supabase = inject(SupabaseClient);
  private router = inject(Router);
  private logger = inject(LoggingService);

  private readonly loginRouterLink = [getRoutePath(loginRoutePath)];

  constructor() {
    this.initializeDeepLinking();
    this.listenForAuthStateChange();
  }

  async ngOnInit() {
    await this.platform.ready();
    this.statusBarService.initialize();
    await this.checkForColdStartDeepLink();
    await this.initializeAppleAuth();
    await this.initializeGoogleAuth();
    await this.lockScreenOrientation();
  }

  private async initializeAppleAuth() {
    if (!this.capacitorService.ios) {
      return;
    }

    if (!config.plugins?.SocialLogin?.apple?.clientId) {
      throw new Error('Missing clientId in capacitor.config.ts');
    }

    await this.platform.ready();

    await this.appleAuthService.initialize();
  }

  /**
   * Check for deep link URL when app is launched from a closed state (cold start).
   * This must be called after platform.ready() in ngOnInit.
   */
  private async checkForColdStartDeepLink() {
    if (!this.capacitorService.mobile) {
      return;
    }

    try {
      const launchUrl = await this.appService.getLaunchUrl();
      if (launchUrl?.url) {
        await this.processDeepLink(launchUrl.url);
      }
    } catch (error) {
      this.logger.error('[DeepLink] Error checking cold start URL:', error);
    }
  }

  /**
   * Initialize listener for deep links when app is already running (hot start).
   */
  private async initializeDeepLinking() {
    if (!this.capacitorService.mobile) {
      return;
    }

    // Wait for platform to be ready before setting up listeners
    return this.platform.ready().then(() => {
      this.appService.addUrlOpenListener(
        async (event: URLOpenListenerEvent) => {
          if (!event.url) {
            return;
          }

          await this.processDeepLink(event.url);
        }
      );
    });
  }

  /**
   * Process a deep link URL and navigate accordingly.
   * Handles both custom heybible:// URLs and standard web URLs.
   */
  private async processDeepLink(urlString: string): Promise<boolean | void> {
    try {
      const url = new URL(urlString);

      // Handle custom heybible:// URL scheme (e.g., from widgets)
      if (url.protocol === 'heybible:') {
        // For custom schemes, the pathname might not parse correctly
        // Extract the path manually from the full URL
        // heybible://verse/992 -> we need to get "verse/992"
        // heybible://purchase-complete?credits=500 -> "purchase-complete"
        const fullPath = urlString.replace('heybible://', '');
        const pathWithoutQuery = fullPath.split('?')[0];
        const queryString = fullPath.split('?')[1] || '';
        const params = new URLSearchParams(queryString);
        const pathParts = pathWithoutQuery
          .split('/')
          .filter((part) => part.length > 0);

        // Handle verse deep links: heybible://verse/:id
        if (pathParts[0] === 'verse' && pathParts[1]) {
          const verseId = pathParts[1];
          return this.router.navigate([
            getFavoritesRoutePath(verseRoutePath),
            verseId,
          ]);
        }

        // Handle purchase completion: heybible://purchase-complete?credits=X
        if (pathParts[0] === 'purchase-complete') {
          // Close the SFSafariViewController that opened Stripe Checkout
          Browser.close().catch(() => {});

          // Refresh the credit balance
          await this.creditsService.refreshBalance();

          const credits = params.get('credits');
          this.alertService.createNotice({
            message: credits
              ? `Payment received! ${credits} credits will appear shortly.`
              : 'Payment received! Your credits will appear shortly.',
            duration: 4000,
            color: 'success',
          });

          // Stay on current page - no navigation needed
          return;
        }

        // Handle purchase cancellation: heybible://purchase-cancelled
        if (pathParts[0] === 'purchase-cancelled') {
          // Close the SFSafariViewController
          Browser.close().catch(() => {});

          this.alertService.createNotice({
            message: 'Purchase cancelled.',
            duration: 3000,
          });
          return;
        }

        // Unknown heybible:// URL - navigate to home
        return this.router.navigate([getRoutePath(searchRoutePath)]);
      }

      // Check for Supabase auth fragments in the URL hash
      if (url.hash) {
        const hashParams = new URLSearchParams(url.hash.substring(1));
        const error = hashParams.get('error');
        const errorCode = hashParams.get('error_code');
        const errorDescription = hashParams.get('error_description');

        // Handle error conditions first
        if (error) {
          let message = errorDescription || 'Unknown error';

          // Provide more user-friendly error messages for specific cases
          if (errorCode === 'otp_expired') {
            message =
              'Your password reset link has expired. Please request a new one.';
          }

          await this.alertService.createNotice({
            message,
            duration: 5000,
            position: 'bottom',
          });

          // Redirect to appropriate page based on error type
          if (errorCode === 'otp_expired') {
            return this.router.navigate([
              getLoginRoutePath(forgotPasswordRoutePath),
            ]);
          } else {
            return this.router.navigate([getRoutePath(loginRoutePath)]);
          }
        }

        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        // Handle password recovery specifically
        if (accessToken && refreshToken && type === 'recovery') {
          try {
            const { error } = await this.supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (error) {
              throw error;
            }

            // Password recovery - redirect to reset password page
            return this.router.navigate([getRoutePath(resetPasswordRoutePath)]);
          } catch (error) {
            this.logger.error(
              '[DeepLink] Failed to set auth session from deep link:',
              error
            );
            // Fall through to normal routing if auth fails
          }
        }
      }

      // Handle checkout redirect Universal Link: https://heybible.app/checkout/redirect?...
      if (url.pathname === '/checkout/redirect') {
        const success = url.searchParams.get('success');
        const cancelled = url.searchParams.get('cancelled');

        if (success === 'true') {
          // Refresh the credit balance
          await this.creditsService.refreshBalance();

          this.alertService.createNotice({
            message: 'Payment received! Your credits will appear shortly.',
            duration: 4000,
            color: 'success',
          });
        } else if (cancelled === 'true') {
          this.alertService.createNotice({
            message: 'Purchase cancelled.',
            duration: 3000,
          });
        }

        // Stay on current page - no navigation needed
        return;
      }

      // Shared post Universal Link: https://heybible.app/p/:id — signed-in
      // users get the full interactive post view; anonymous visitors fall
      // through to the public share page.
      const publicPostMatch = url.pathname.match(/^\/p\/([0-9a-f-]{36})$/i);
      if (publicPostMatch) {
        const user = await this.userService.getUser().catch(() => null);

        if (user?.id) {
          return this.router.navigate([
            getRoutePath(feedRoutePath),
            postRoutePath,
            publicPostMatch[1],
          ]);
        }
      }

      // Normal deep link routing (no auth tokens or auth failed)
      const route = url.pathname + url.search + url.hash;
      if (!route || route === '/') {
        return;
      }

      return this.router.navigateByUrl(route);
    } catch (error) {
      this.logger.error('[DeepLink] Error handling universal link:', error);
      // Don't throw - fail gracefully
      return false;
    }
  }

  private async initializeGoogleAuth() {
    if (!this.capacitorService.mobile) {
      return;
    }

    if (!config.plugins?.SocialLogin?.google?.webClientId) {
      throw new Error('Missing webClientId in capacitor.config.ts');
    }

    if (!config.plugins?.SocialLogin?.google?.iOSClientId) {
      throw new Error('Missing iOSClientId in capacitor.config.ts');
    }

    await this.platform.ready();

    await this.googleAuthService.initialize();
  }

  private async lockScreenOrientation() {
    if (!this.capacitorService.mobile) {
      return;
    }

    await this.screenOrientation.lock('portrait-primary');
  }

  private listenForAuthStateChange() {
    this.supabase.auth.onAuthStateChange((event) => {
      // Prevent supabase deadlocks with setTimeout
      // See https://supabase.com/docs/reference/javascript/auth-signup#auth-onauthstatechange
      setTimeout(async () => {
        if (event === 'SIGNED_OUT') {
          await this.router.navigate(this.loginRouterLink);
          this.cdr.detectChanges(); // Manual change detection for zoneless mode
          return;
        }

        if (event === 'PASSWORD_RECOVERY') {
          // Redirect to reset password page when password recovery link is clicked
          await this.router.navigate([getRoutePath(resetPasswordRoutePath)]);
          this.cdr.detectChanges(); // Manual change detection for zoneless mode
          return;
        }

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // Sync favorites to widget on sign-in
          await this.syncWidgetOnLogin();
        }
      }, 0);
    });
  }

  /**
   * Sync favorites to iOS widget after user signs in.
   * Uses lazy injection to avoid circular dependencies.
   */
  private async syncWidgetOnLogin(): Promise<void> {
    if (!this.capacitorService.ios) {
      return;
    }

    try {
      const { FavoritesService } = await import(
        './favorites/favorites.service'
      );
      const favoritesService = this.injector.get(FavoritesService);
      await favoritesService.syncToWidget();
    } catch (error) {
      // Silently fail - widget sync is not critical
      this.logger.error('[Widget] Failed to sync on login:', error);
    }
  }
}
